/**
 * @fileoverview Utility functions for generating data-testid from editPath
 * @module screen-view-v2/utils/testIdUtils
 *
 * Provides consistent testId generation based on schema paths for E2E testing.
 * The testId matches the editor field paths, enabling unified selectors across
 * the screen-view-v2 rendering and reactFlow editor modules.
 *
 * All screen-view-v2 testIds are prefixed with 'view-' to distinguish them from
 * editor testIds (which use 'section-', 'field-', 'input-' prefixes).
 *
 * Key v1 → v2 change: 'struct.' prefix → 'content.' prefix
 *
 * ## When TestIds Are Generated
 *
 * TestIds are generated in screen components when:
 * - editMode=true (editor mode with click-to-edit)
 * - previewMode='live' (web-shell production mode for E2E testing)
 *
 * TestIds are NOT generated when:
 * - previewMode='static' (thumbnails/canvas nodes - no need for testIds)
 *
 * ## Usage Pattern in Screen Components
 *
 * ```typescript
 * const ep = editMode || previewMode === 'live' ? 'content' : undefined;
 * <Footer editPath={ep ? `${ep}.actionBar` : undefined} />
 * ```
 */

/** Prefix for all screen-view-v2 data-testid values */
export const VIEW_TESTID_PREFIX = 'view-';

/**
 * Extract schema path from editPath for data-testid attribute.
 *
 * Removes 'content.' prefix if present and adds 'view-' prefix.
 * This allows E2E tests to locate elements using schema-based selectors
 * that match the editor field configuration, while distinguishing view
 * elements from editor elements.
 *
 * @param editPath - Full edit path (e.g., "content.header.back")
 * @returns Schema path with view prefix (e.g., "view-header.back"), or undefined
 *
 * @example
 * getSchemaTestId('content.header.back')       // → 'view-header.back'
 * getSchemaTestId('content.actionBar.primary')  // → 'view-actionBar.primary'
 * getSchemaTestId('content.options.items.0')    // → 'view-options.items.0'
 * getSchemaTestId(undefined)                    // → undefined
 * getSchemaTestId('header.back')                // → 'view-header.back' (no content prefix)
 */
export function getSchemaTestId(editPath: string | undefined): string | undefined {
  if (!editPath) return undefined;
  const schemaPath = editPath.startsWith('content.') ? editPath.slice(8) : editPath;
  return `${VIEW_TESTID_PREFIX}${schemaPath}`;
}
